import { useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { Plus } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'
import { SectionErrorState } from '../SectionErrorState'
import { RewardItemCard } from './RewardItemCard'
import { RewardItemModal } from './RewardItemModal'
import type { RewardItem, SaveRewardItemInput } from './types'

const SKELETON_CARD_CLASSNAME =
  'w-[calc(50%_-_6px)] lg:w-[calc(33.333%_-_8px)] 2xl:w-[calc(50%_-_6px)] 3xl:w-[calc(33.333%_-_8px)] min-h-[160px] animate-pulse rounded-lg bg-canvas-primary'

interface RewardItemsGridProps {
  i18nPrefix: string
  rewardItems: RewardItem[]
  assignedRewardItemIds: Set<string>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onSaveItem: (input: SaveRewardItemInput) => Promise<unknown>
  isSaving: boolean
  isSaveError: boolean
  onResetSaveState?: () => void
  disabled?: boolean
}

export function RewardItemsGrid({
  i18nPrefix,
  rewardItems,
  assignedRewardItemIds,
  isLoading,
  isError,
  onRetry,
  onSaveItem,
  isSaving,
  isSaveError,
  onResetSaveState,
  disabled = false,
}: RewardItemsGridProps) {
  const { t } = useTranslation()
  const [editingItem, setEditingItem] = useState<RewardItem | 'new' | null>(null)

  function openEditor(item: RewardItem | 'new') {
    if (disabled) return
    onResetSaveState?.()
    setEditingItem(item)
  }

  return (
    <View className={disabled ? 'opacity-50' : undefined}>
      {isError ? (
        <SectionErrorState message={t(`${i18nPrefix}.loadError`)} retryLabel={t('common.retry')} onRetry={onRetry} />
      ) : (
        <View className="flex-row flex-wrap gap-3">
          <Pressable
            onPress={() => openEditor('new')}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={t(`${i18nPrefix}.add`)}
            accessibilityState={{ disabled }}
            className="w-[calc(50%_-_6px)] lg:w-[calc(33.333%_-_8px)] 2xl:w-[calc(50%_-_6px)] 3xl:w-[calc(33.333%_-_8px)] min-h-[160px] items-center justify-center gap-2 rounded-lg border border-dashed border-accent/40 bg-accent/5 p-4"
          >
            <View className="h-10 w-10 items-center justify-center rounded-full bg-accent/10">
              <Plus size={20} color={colors.accent.DEFAULT} strokeWidth={2} />
            </View>
            <Text className="text-center text-sm font-semibold text-accent">{t(`${i18nPrefix}.add`)}</Text>
            <Text className="text-center text-xs text-secondary">{t(`${i18nPrefix}.addDescription`)}</Text>
          </Pressable>

          {isLoading ? (
            <>
              <View className={SKELETON_CARD_CLASSNAME} />
              <View className={SKELETON_CARD_CLASSNAME} />
            </>
          ) : (
            rewardItems.map((item) => (
              <RewardItemCard key={item.id} item={item} i18nPrefix={i18nPrefix} onPress={openEditor} />
            ))
          )}
        </View>
      )}

      <RewardItemModal
        i18nPrefix={i18nPrefix}
        item={editingItem}
        isAssigned={editingItem !== 'new' && editingItem !== null && assignedRewardItemIds.has(editingItem.id)}
        isSaving={isSaving}
        isSaveError={isSaveError}
        onSave={onSaveItem}
        onClose={() => setEditingItem(null)}
      />
    </View>
  )
}
